import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck, User, Clock, Tag } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { EntityAutocomplete } from '@/components/ui/entity-autocomplete';
import { PageHeader } from '@/components/ui/page-header';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { cn, formatDate } from '@/lib/utils';
import api from '@/lib/api';
import { auditEntityLabel } from '@/lib/audit-labels';
import { RecordLink } from '@/components/records/RecordLink';
import { useRowOpen } from '@/components/records/useRowOpen';
import { DELETED_RECORD, humanizeEntity } from '@/components/records/AuditEntryDetailBody';
import type { RecordType } from '@/components/records/registry';

interface UserOption {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}

interface AuditLogRow {
  id: string;
  createdAt: string;
  userId?: string | null;
  user?: { firstName?: string | null; lastName?: string | null; email?: string | null } | null;
  action: string;
  entity: string;
  entityId?: string | null;
  /** POS-I239: readable name or number of the record (API), and the viewer type when it can be opened. */
  entityLabel?: string | null;
  entityName?: string | null;
  movementType?: string | null;
  recordType?: string | null;
  oldValues?: Record<string, unknown> | null;
  newValues?: Record<string, unknown> | null;
}

const ACTION_STYLES: Record<string, string> = {
  CREATE: 'bg-success/10 text-success-text ring-1 ring-success',
  UPDATE: 'bg-info/10 text-info-text ring-1 ring-info',
  DELETE: 'bg-destructive/10 text-destructive-text ring-1 ring-destructive',
  VOID: 'bg-warning/10 text-warning-text ring-1 ring-warning',
  LOGIN: 'bg-primary/10 text-primary-text ring-1 ring-primary',
  LOGOUT: 'bg-muted text-muted-foreground ring-1 ring-border',
};

const ENTITY_OPTIONS = [
  { value: '', label: 'All Entities' },
  { value: 'Sale', label: 'Sale' },
  { value: 'Product', label: 'Product' },
  { value: 'Expense', label: 'Expense' },
  { value: 'PurchaseOrder', label: 'Purchase Order' },
  { value: 'User', label: 'User' },
  { value: 'Tenant', label: 'Tenant' },
  { value: 'Promotion', label: 'Promotion' },
  { value: 'FbrSubmission', label: 'FBR Submission' },
  { value: 'VendorPayment', label: 'Vendor Payment' },
];

const ACTION_OPTIONS = [
  { value: '', label: 'All Actions' },
  { value: 'CREATE', label: 'Create' },
  { value: 'UPDATE', label: 'Update' },
  { value: 'DELETE', label: 'Delete' },
  { value: 'VOID', label: 'Void' },
  { value: 'LOGIN', label: 'Login' },
  { value: 'LOGOUT', label: 'Logout' },
];

export function AuditPage() {
  const { t } = useTranslation('settings');
  const { t: tr } = useTranslation('records');
  const { t: tl } = useTranslation('mobileLinks');
  const { t: tInv } = useTranslation('inventory');
  const rowOpen = useRowOpen();
  const [page, setPage] = useState(1);
  const [entityFilter, setEntityFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['audit-logs', page, entityFilter, actionFilter, userIdFilter],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '30' });
      if (entityFilter) params.append('entity', entityFilter);
      if (actionFilter) params.append('action', actionFilter);
      if (userIdFilter) params.append('userId', userIdFilter);
      const res = await api.get(`/audit-logs?${params.toString()}`);
      return res.data;
    },
    staleTime: 30_000,
  });

  const logs: AuditLogRow[] = data?.data ?? [];
  const meta = data?.meta ?? {};

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader title="Audit Log" subtitle="Track all actions performed in your account" />

      {/* Filters */}
      <Card className="animate-fade-in-up">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Combobox
              options={ENTITY_OPTIONS}
              value={entityFilter}
              onChange={(v) => { setEntityFilter(v || ''); setPage(1); }}
              placeholder="Filter by entity..."
            />
            <Combobox
              options={ACTION_OPTIONS}
              value={actionFilter}
              onChange={(v) => { setActionFilter(v || ''); setPage(1); }}
              placeholder="Filter by action..."
            />
            <EntityAutocomplete<UserOption>
              endpoint="/users"
              value={selectedUser}
              onChange={(user) => {
                setSelectedUser(user);
                setUserIdFilter(user?.id ?? '');
                setPage(1);
              }}
              getLabel={(u) => `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || (u.email ?? '')}
              itemTemplate={(u) => (
                <div className="flex flex-col py-0.5">
                  <span className="font-medium text-sm">{`${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || (u.email ?? '—')}</span>
                  {u.email && <span className="text-xs text-muted-foreground">{u.email}</span>}
                </div>
              )}
              placeholder="Filter by user…"
            />
          </div>
        </CardContent>
      </Card>

      {/* Log table */}
      <Card className="animate-fade-in-up">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table mobileCards>
              <THead>
                <Tr hover={false}>
                  <Th>
                    <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{t('tblTimestamp')}</span>
                  </Th>
                  <Th>
                    <span className="flex items-center gap-1.5"><User className="h-3.5 w-3.5" />{t('tblUser')}</span>
                  </Th>
                  <Th>{t('tblAction')}</Th>
                  <Th>
                    <span className="flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" />{t('tblEntity')}</span>
                  </Th>
                  <Th>{tr('auditRecord')}</Th>
                  <Th>{t('tblDetails')}</Th>
                </Tr>
              </THead>
              <TBody>
                {isLoading ? (
                  <Tr hover={false} className="no-card">
                    <Td colSpan={6} className="py-10 text-center text-muted-foreground">
                      Loading audit logs…
                    </Td>
                  </Tr>
                ) : logs.length === 0 ? (
                  <Tr hover={false} className="no-card">
                    <Td colSpan={6} className="py-10 text-center text-muted-foreground">
                      <ShieldCheck className="mx-auto h-10 w-10 mb-2 opacity-25" />
                      No audit logs found
                    </Td>
                  </Tr>
                ) : (
                  logs.map((log) => (
                    <Tr key={log.id} {...rowOpen('auditEntry', log.id, humanizeEntity(log.entity))}>
                      <Td className="text-xs text-muted-foreground whitespace-nowrap td-name">
                        {formatDate(log.createdAt)}
                      </Td>
                      <Td label={t('tblUser')}>
                        {log.user ? (
                          <div>
                            <p className="font-medium text-xs">{log.user.firstName} {log.user.lastName}</p>
                            <p className="text-[11px] text-muted-foreground">{log.user.email}</p>
                            {typeof log.newValues?.viaMobileLinkName === 'string' && (
                              <p className="text-[11px] text-info-text">{tl('viaLink', { name: log.newValues.viaMobileLinkName })}</p>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">{tr('unknownUser')}</span>
                        )}
                      </Td>
                      <Td label={t('tblAction')}>
                        <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium', ACTION_STYLES[log.action] ?? 'bg-muted text-muted-foreground')}>
                          {log.action}
                        </span>
                      </Td>
                      <Td className="text-xs font-medium" label={t('tblEntity')}>{humanizeEntity(log.entity)}</Td>
                      <Td className="text-xs td-hide" label={tr('auditRecord')}>
                        {auditEntityLabel(log, tInv) && log.recordType && log.entityId ? (
                          <RecordLink type={log.recordType as RecordType} id={log.entityId} label={auditEntityLabel(log, tInv) ?? ''} variant="mono" className="text-xs" />
                        ) : auditEntityLabel(log, tInv) ? (
                          <span className="break-words">{log.entityLabel === DELETED_RECORD ? tr('deletedRecord') : auditEntityLabel(log, tInv)}</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </Td>
                      <Td className="text-xs text-muted-foreground max-w-xs truncate td-hide" label={t('tblDetails')}>
                        {(() => {
                          const oldV = log.oldValues ?? {};
                          const newV = log.newValues ?? {};
                          const keys = Array.from(new Set([...Object.keys(oldV), ...Object.keys(newV)]))
                            .filter((k) => JSON.stringify(oldV[k]) !== JSON.stringify(newV[k]));
                          if (keys.length === 0) return '—';
                          const summary = keys.slice(0, 3).join(', ') + (keys.length > 3 ? `, +${keys.length - 3}` : '');
                          const detail = keys
                            .map((k) => `${k}: ${JSON.stringify(oldV[k]) ?? '—'} → ${JSON.stringify(newV[k]) ?? '—'}`)
                            .join('\n');
                          return <span title={detail}>{summary}</span>;
                        })()}
                      </Td>
                    </Tr>
                  ))
                )}
              </TBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {meta.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {meta.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= meta.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
